/**
 * Which group region is under a point, resolved on the CPU.
 *
 * Alongside `findNetworkNodeAt` rather than inside it, because the two answer different questions
 * and a caller has to ask them in order. A mark is a thing; a region is the space around things,
 * and the space around things is almost always also inside something. Merged into one search they
 * would compete on the same depth tie-break, and a cloud drawn in front of a mark would win the
 * pointer that was aimed at the mark.
 *
 * The same projection the shader uses, for the reason `findNetworkNodeAt` gives: this has to agree
 * with what was drawn rather than approximate it.
 */

import type { Size, ViewCamera } from '../../types';
import {
  applyViewProjection,
  clipToScreen,
  toViewProjectionMatrix,
} from '../../geometry/viewProjection';
import { VOLUME_FLOATS, type NetworkInstances } from './packNetwork';

export type FindNetworkVolumeArgs = {
  instances: NetworkInstances;
  camera: ViewCamera;
  /** In CSS pixels, matching the pointer coordinates. */
  viewport: Size;
  /** Cursor position within the canvas, in CSS pixels. */
  point: { x: number; y: number };
};

/**
 * The innermost region containing the point, as an index into `layout.volumes`.
 *
 * Innermost by *drawn* size rather than by `depth`, and the difference is the whole of the
 * resolution. Nesting depth says where a region sits in the tree, which is not where it sits under
 * the cursor: two disjoint regions at the same depth overlap in screen space from most angles, and
 * a deep region can be the larger of the two when its parent has one big child and one small one.
 * The smallest disc under the pointer is the one a reader is pointing at, whatever the tree says.
 *
 * No tolerance, unlike a mark. A region is hundreds of pixels across and its edge is a gradient
 * rather than a boundary, so growing the target buys nothing and costs the gap between two
 * neighbouring clouds - the one place a reader can get at the estate behind them.
 */
export const findNetworkVolumeAt = ({
  instances,
  camera,
  viewport,
  point,
}: FindNetworkVolumeArgs): number | null => {
  if (instances.volumeCount === 0) return null;
  if (viewport.width === 0 || viewport.height === 0) return null;

  const matrix = toViewProjectionMatrix(camera, viewport);
  const pixelsPerUnit =
    (Math.min(viewport.width, viewport.height) / 2) * camera.zoom;

  let closest: number | null = null;
  let smallest = Infinity;

  for (let index = 0; index < instances.volumeCount; index++) {
    const at = index * VOLUME_FLOATS;
    const clip = applyViewProjection(
      matrix,
      instances.volumes[at],
      instances.volumes[at + 1],
      instances.volumes[at + 2]
    );
    /* Behind the eye. Its projection wraps around the origin and would claim the whole viewport. */
    if (clip.w <= 0) continue;

    const { screenX, screenY } = clipToScreen(clip, viewport);
    const drawn = (instances.volumes[at + 3] * pixelsPerUnit) / clip.w;
    if (drawn <= 0 || drawn >= smallest) continue;

    if (Math.hypot(screenX - point.x, screenY - point.y) <= drawn) {
      smallest = drawn;
      closest = index;
    }
  }

  return closest;
};
